/**
 * content.ts (T27.2.10.2.1) — the public legal pages' content source.
 *
 * The legal text is no longer bundled at build time. It is fetched at runtime from
 * the backend's published copy (`GET /api/legal/:slug`), so an admin's Publish
 * updates the live pages with no app/web release (the decoupling invariant). This
 * module owns the slug → title metadata and the fetch; `LegalDocPage` renders the
 * fetched Markdown with loading + error states.
 *
 * The privacy / terms / eula / cookies drafts live in `docs/legal/drafts/` and are
 * inlined into the BACKEND at build time, then published into the DB. The web app
 * no longer crosses the `docs/legal/` boundary (the `?raw` import + `fs.allow`
 * widening are gone).
 */

export type LegalSlug = 'privacy' | 'terms' | 'eula' | 'cookies';

export interface LegalDocMeta {
  /** The public route slug (matches routes.* without the leading slash). */
  slug: LegalSlug;
  /** Short label for the `/legal` index + back-link + document title. */
  title: string;
}

export const LEGAL_DOCS: Record<LegalSlug, LegalDocMeta> = {
  privacy: { slug: 'privacy', title: 'Privacy Policy' },
  terms: { slug: 'terms', title: 'Terms of Service' },
  eula: { slug: 'eula', title: 'End User License Agreement' },
  cookies: { slug: 'cookies', title: 'Cookie Policy' },
};

/** The published-document shape returned by `GET /api/legal/:slug`. */
export interface PublishedLegalDoc {
  slug: string;
  content: string;
  version: number;
  published_at: string | null;
  published_by: string | null;
}

// Same API-base idiom the other public surfaces use (LoginVersionFooter, admin
// api.ts): an env override, else localhost for dev. The legal pages mount OUTSIDE
// the ApiProvider (no session), so this fetch is standalone — no HttpClient.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3001';

/**
 * Fetch one published legal document's Markdown content. Rejects (so the page can
 * show its error state) on a non-2xx response or a network failure — never returns
 * an empty body silently.
 */
export async function fetchLegalDoc(slug: LegalSlug): Promise<PublishedLegalDoc> {
  const res = await fetch(`${API_BASE_URL}/api/legal/${encodeURIComponent(slug)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`GET /api/legal/${slug} failed: ${res.status}`);
  }
  return (await res.json()) as PublishedLegalDoc;
}

/**
 * The published privacy contact, mirroring `privacy-policy.md` § Contact Us. The
 * delete-account page names this as the deletion-request fallback for users who
 * cannot self-serve. NOT a draft — a hand-built React component reads it, so it
 * stays a constant (not fetched).
 */
export const CONTACT_EMAIL = 'legal@shopista.ca';

/**
 * The publishing entity, mirroring `privacy-policy.md` § Who We Are
 * ("operated by Aktok Inc., … operating as 'Shopista'"). NOT a draft — a constant.
 */
export const LEGAL_ENTITY = 'Aktok Inc.';
